<script setup lang="ts">
import {computed, onUnmounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import {ChevronLeft, Loader2, Sparkles} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {useToastStore} from "@/stores/toast.store";
import useAssistantScreen from "@/ai-assistant/composables/useAssistantScreen";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {PLANNING_ASSISTANT} from "@/ai-assistant/constants";
import {appointmentApi, healthJourneyApi} from "@/container";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {AppointmentCreateRequest} from "@/core/http/resources/AppointmentResource";
import {durationForBooker} from "@/appointment/lib/bookerAppointment";
import {Customer} from "@/customer/interfaces/Customer";
import {Program} from "@/program/interfaces/Program";
import {SelectedPatient} from "@/agenda/components/AgendaPatientSearch.vue";
import HealthJourneyDoneDialog from "@/agenda/components/health-journey/HealthJourneyDoneDialog.vue";
import HealthJourneyPatientStep from "@/agenda/components/health-journey/HealthJourneyPatientStep.vue";
import HealthJourneyPlanEditor from "@/agenda/components/health-journey/HealthJourneyPlanEditor.vue";
import HealthJourneyProgramsStep from "@/agenda/components/health-journey/HealthJourneyProgramsStep.vue";
import HealthJourneyRulesPanel from "@/agenda/components/health-journey/HealthJourneyRulesPanel.vue";
import {actsOfProgram, JourneyAct, MAX_ACTS, recapWarning, toActRequests} from "@/agenda/lib/healthJourney";
import {isPastStart} from "@/agenda/lib/agendaTime";
import {PAST_APPOINTMENT_MESSAGE} from "@/agenda/constants";
import {HealthJourneyPinRequest, HealthJourneyProposal, HealthJourneyStep} from "@/agenda/interfaces/healthJourney";

/**
 * The health journey screen: a patient, the programs they are targeted by,
 * and the series of appointments that covers them, dated in one pass.
 *
 * The planning server lays the acts out and says why; nothing of the patient
 * ever reaches it — each act travels under a reference minted here. The
 * appointments themselves are written on the health server, at the end, in a
 * single batch that also opens the journey and texts its recap.
 */
const route = useRoute();
const router = useRouter();
const toastStore = useToastStore();
const organizationId = useOrganizationId();

useAssistantScreen(() => ({screen: 'Parcours officinal de santé'}));

const patient = ref<SelectedPatient | null>(null);
const customer = ref<Customer | null>(null);
const programs = ref<Program[]>([]);

/**
 * Acts the pharmacist took out of the series, by reference. Kept apart from
 * the programs so unticking and reticking a program does not silently bring
 * back an act they removed on purpose.
 */
const removed = ref<string[]>([]);

/**
 * Acts the pharmacist added themselves, outside what the patient is targeted
 * by. Held apart from the programs so unticking one never takes them away.
 */
const extraActs = ref<JourneyAct[]>([]);

/** Moments the pharmacist froze, which the planner must keep. */
const pins = ref<Record<string, {date: string; start: string; member_id: number | null}>>({});

const proposal = ref<HealthJourneyProposal | null>(null);
const generating = ref(false);
const generationError = ref<string | null>(null);
const programming = ref(false);

/**
 * What was written, once it has been.
 *
 * Holds the screen in its finished state: the plan has become appointments,
 * so nothing here may be programmed again, and the pharmacist is offered the
 * printable recap before leaving.
 */
const programmed = ref<{count: number; notice: string | null} | null>(null);
const doneOpen = ref(false);

/**
 * The question the plan on screen answers.
 *
 * A plan is only an answer to what was asked. Ticking a program off, freezing
 * a moment or releasing one all change the question, and the appointments
 * must not be written from an answer to the previous one. Held as the whole
 * question and not only its acts: a frozen moment does not change the list of
 * acts, and a plan that ignores it is just as wrong.
 */
const answering = ref<string | null>(null);

/**
 * Identity of the most recent request, and how many are still out.
 *
 * A proposal is a solver pass over a quarter of agenda; two of them started a
 * second apart do not come back in that order. Without this, the answer to a
 * superseded request would overwrite the current plan, and the pharmacist
 * would programme a series they had already changed.
 */
let latestRequest = 0;
let pending = 0;

/** Every act the ticked programs contribute, before the pharmacist prunes them. */
const offered = computed<JourneyAct[]>(() => [
    ...programs.value.flatMap((program) => actsOfProgram(program)),
    ...extraActs.value
]);

const acts = computed<JourneyAct[]>(() =>
    offered.value.filter((act) => !removed.value.includes(act.clientRef))
);

const references = computed<string[]>(() => acts.value.map((act) => act.clientRef));

/**
 * The question, as a string: the acts asked for and the moments frozen.
 *
 * Compared by content and not by identity, because the lists are rebuilt on
 * every prune — an identity watch would re-ask a question that has not
 * actually changed.
 */
const question = computed<string>(() => [
    references.value.join('|'),
    Object.entries(pins.value)
        .map(([reference, moment]) => `${reference}@${moment.date}T${moment.start}#${moment.member_id ?? ''}`)
        .sort()
        .join('|')
].join('~'));

/** Whether the plan on screen answers a question the pharmacist has since changed. */
const planStale = computed<boolean>(() => !!proposal.value && answering.value !== question.value);

/**
 * Forget what was decided about acts no longer on offer.
 *
 * Unticking a program is how the pharmacist takes back a series entirely, so
 * ticking it again has to bring it back whole — otherwise an act removed by a
 * mis-click could never be recovered.
 */
watch(offered, (current) => {
    const references = current.map((act) => act.clientRef);

    removed.value = removed.value.filter((reference) => references.includes(reference));
    pins.value = Object.fromEntries(
        Object.entries(pins.value).filter(([reference]) => references.includes(reference))
    );
});

const addAct = (act: JourneyAct) => {
    if (offered.value.some((entry) => entry.clientRef === act.clientRef)) {
        // Already in the series, whether from its program or from a previous
        // addition: adding it twice would send the same reference twice, which
        // the planner refuses, and would book the patient the same act twice.
        removed.value = removed.value.filter((reference) => reference !== act.clientRef);

        return;
    }

    extraActs.value = [...extraActs.value, act];
};

/** A patient change starts a new journey: nothing of the previous one holds. */
watch(() => patient.value?.id ?? null, (id, previous) => {
    if (id === previous) {
        return;
    }

    // Supersedes whatever is already out: an answer computed for the patient
    // just replaced would otherwise land and fill the screen under the new one.
    latestRequest++;

    recompute.cancel();
    programs.value = [];
    extraActs.value = [];
    removed.value = [];
    pins.value = {};
    proposal.value = null;
    answering.value = null;
    generationError.value = null;

    // Clearing what was written unmounts the confirmation window, which is
    // what makes the screen usable again. `doneOpen` is deliberately left
    // alone: closing that window is how the pharmacist says they are leaving,
    // and forcing it shut here would read as such and navigate away under them.
    programmed.value = null;
});

/**
 * Opened from a patient file or from the appointment hub: the patient is named
 * in the query and the search step reads their file from it.
 */
watch(() => route.query.customerId, (value) => {
    const id = Number(value);

    if (id && id !== patient.value?.id) {
        patient.value = {id, name: ''};
    }
}, {immediate: true});

const recapNote = computed(() => {
    if (!customer.value?.information?.phone) {
        return 'Le patient n’a pas de mobile : il ne recevra pas de récapitulatif.';
    }

    return 'Le patient recevra un SMS récapitulatif de son parcours, depuis lequel il pourra annuler un rendez-vous.';
});

/**
 * Ask the planner for a journey.
 *
 * `wording` is turned off while the pharmacist edits: a removal or a frozen
 * moment has to show its effect at once, and re-wording the whole answer on
 * every adjustment would make the screen wait on a model for a change it
 * already knows the shape of.
 */
const generate = async (wording = true) => {
    if (!organizationId.value || !acts.value.length) {
        return;
    }

    // A full generation answers everything a queued silent one would have.
    recompute.cancel();

    const request = ++latestRequest;
    const askedActs = references.value;
    const askedQuestion = question.value;
    const today = dayjs().format('YYYY-MM-DD');

    pending++;
    generating.value = true;
    generationError.value = null;

    // A pin on an act that is no longer asked for freezes nothing, and one on
    // a past day cannot be honoured: both are refused by the server, so they
    // are dropped here rather than costing the pharmacist their whole request.
    const pinned: HealthJourneyPinRequest[] = Object.entries(pins.value)
        .filter(([reference, moment]) => askedActs.includes(reference) && moment.date >= today)
        .map(([reference, moment]) => ({
            client_ref: reference,
            date: moment.date,
            start: moment.start,
            member_id: moment.member_id
        }));

    try {
        const answer = await healthJourneyApi().propose(organizationId.value, PLANNING_ASSISTANT, {
            from: today,
            acts: toActRequests(acts.value),
            pinned,
            wording
        });

        if (request !== latestRequest) {
            return;
        }

        proposal.value = answer;
        answering.value = askedQuestion;
    } catch (error) {
        if (request !== latestRequest) {
            return;
        }

        if (!isAuthorizationError(error)) {
            generationError.value = apiErrorMessage(error);
        }
    } finally {
        pending--;
        generating.value = pending > 0;
    }
};

/**
 * Bring the plan back in line with what is asked for.
 *
 * Debounced because an adjustment comes in bursts — a stepper pressed five
 * times, two acts taken out in a row — and each press would otherwise cost a
 * full solver pass on a throttled endpoint. Silent: the wording is re-asked
 * for by the « Recalculer » button, not by every keystroke.
 */
const recompute = debounce(() => {
    if (proposal.value) {
        void generate(false);
    }
}, 400);

onUnmounted(() => recompute.cancel());

/**
 * Every change to the question re-asks it: acts ticked, unticked, added or
 * removed, and moments frozen or released. Nothing on screen may claim to
 * answer a question that has moved on.
 */
watch(question, () => {
    // Nothing left to lay out: the plan on screen answers a question nobody is
    // asking any more, and saying it is « being recomputed » would promise a
    // pass that `generate` declines to run.
    if (!acts.value.length) {
        recompute.cancel();
        proposal.value = null;
        answering.value = null;
        generationError.value = null;

        return;
    }

    recompute();
});

const handlePin = (moment: {clientRef: string; date: string; start: string; member_id: number | null}) => {
    pins.value = {
        ...pins.value,
        [moment.clientRef]: {date: moment.date, start: moment.start, member_id: moment.member_id}
    };
};

const handleUnpin = (clientRef: string) => {
    const next = {...pins.value};
    delete next[clientRef];
    pins.value = next;
};

const handleRemove = (clientRef: string) => {
    removed.value = [...removed.value, clientRef];
    handleUnpin(clientRef);
};

/** An act with no moment cannot be written; it is left out and said so. */
const placedSteps = computed<HealthJourneyStep[]>(() =>
    (proposal.value?.plan.steps ?? []).filter((step) => step.status === 'placed' && step.date && step.start)
);

/**
 * The appointment one placed act becomes.
 *
 * The act is passed in rather than looked up here: its booker carries the
 * title, the description and the duration, and an appointment built without
 * it would silently land as a generic « Rendez-vous santé » of fifteen
 * minutes, attached to no program.
 */
const appointmentFor = (step: HealthJourneyStep, act: JourneyAct, customerId: number): AppointmentCreateRequest => {
    const duration = durationForBooker(act.booker);

    return {
        title: act.booker?.appointment_title || 'Rendez-vous santé',
        description: act.booker?.appointment_description || 'Rendez-vous santé',
        customer_id: customerId,
        // Wall-clock, as the counter reads it: the agenda's own creation form
        // sends the same shape for a moment the pharmacist picked on screen.
        start_at: `${step.date} ${step.start}:00`,
        ...(step.appointment_type_id ? {appointment_type_id: step.appointment_type_id} : {}),
        ...(duration !== undefined ? {duration} : {}),
        operator_access_point_id: step.member_id,
        beneficiary_id: null,
        ...(act.programId ? {appointmentable_type: 'support', appointmentable_id: act.programId} : {})
    };
};

const submit = async () => {
    const organization = organizationId.value;
    const patientId = customer.value?.id;

    if (!organization || !patientId || !proposal.value || planStale.value || !placedSteps.value.length) {
        return;
    }

    if (placedSteps.value.some((step) => isPastStart(`${step.date}T${step.start}`))) {
        toastStore.showError({content: PAST_APPOINTMENT_MESSAGE});

        return;
    }

    // Written once. The confirmation stays in the way until the screen is
    // left, but a second press before it opens would book the patient twice.
    if (programmed.value) {
        return;
    }

    // Every placed act must still be one of the acts asked for. `planStale`
    // already says so, and this is what makes that guarantee a fact rather
    // than an invariant somebody has to remember.
    const requests: AppointmentCreateRequest[] = [];

    for (const step of placedSteps.value) {
        const act = acts.value.find((entry) => entry.clientRef === step.client_ref);

        if (!act) {
            toastStore.showError({
                content: 'Le parcours affiché ne correspond plus aux programmes choisis. Recalculez-le avant de le programmer.'
            });

            return;
        }

        requests.push(appointmentFor(step, act, patientId));
    }

    programming.value = true;
    const notifyRecap = !!customer.value?.information?.phone;

    try {
        const result = await appointmentApi().createHealthJourneyBatch(organization, requests, {
            summary: proposal.value.summary?.text ?? null,
            notify_recap: notifyRecap
        });

        programmed.value = {
            count: result.appointments.length,
            notice: notifyRecap
                ? recapWarning(result.healthJourney?.recap ?? null)
                : 'Le patient n’a pas de mobile : aucun récapitulatif ne lui a été envoyé.'
        };
        doneOpen.value = true;
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }
    } finally {
        programming.value = false;
    }
};

/** The journey is written; the screen has nothing left to do. */
const leave = () => {
    void router.push({name: 'agenda'});
};

const unplaced = computed(() => {
    const plan = proposal.value?.plan;

    return plan ? plan.kpis.estimated + plan.kpis.unplaced : 0;
});

const canGenerate = computed(() =>
    !!acts.value.length && acts.value.length <= MAX_ACTS && !generating.value
);
</script>

<template>
  <div class="flex h-full flex-col gap-4 p-6">
    <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div class="flex items-center gap-3">
        <router-link :to="{name: 'agenda'}">
          <Button variant="ghost" size="icon" aria-label="Retour à l'agenda">
            <ChevronLeft class="size-4" />
          </Button>
        </router-link>
        <div>
          <h1 class="text-lg font-semibold text-primary">
            Parcours officinal de santé
          </h1>
          <p class="flex items-center gap-1.5 text-xs text-gray-500">
            <Sparkles class="size-3.5" />
            Une série de rendez-vous datée à partir des programmes qui ciblent le patient
          </p>
        </div>
      </div>
    </div>

    <div class="grid min-h-0 flex-1 gap-4 lg:grid-cols-[22rem_1fr]">
      <div class="flex min-h-0 flex-col gap-3 overflow-y-auto">
        <HealthJourneyPatientStep v-model:patient="patient" v-model:customer="customer" />
        <HealthJourneyProgramsStep
          v-model:selected="programs"
          :consumer-key="patient?.consumerKey ?? null"
          :act-count="acts.length"
          @add="addAct"
        />
        <HealthJourneyRulesPanel @changed="recompute" />
        <Button
          class="bg-primary"
          :disabled="!canGenerate"
          @click="generate(true)"
        >
          <Loader2 v-if="generating" class="mr-2 size-4 animate-spin" />
          {{ proposal ? 'Regénérer le parcours' : 'Générer le parcours' }}
        </Button>
        <p v-if="acts.length > MAX_ACTS" class="text-xs text-gray-600">
          Un parcours ne peut pas dépasser {{ MAX_ACTS }} rendez-vous. Retirez un programme.
        </p>
        <p v-else-if="!acts.length" class="text-xs text-gray-600">
          {{ patient ? 'Cochez au moins un programme comportant des rendez-vous, ou ajoutez-en un ci-dessus.'
            : 'Sélectionnez un patient pour commencer.' }}
        </p>
      </div>

      <div class="flex min-h-0 flex-col gap-3">
        <p
          v-if="generationError"
          class="rounded-lg bg-white p-4 text-sm text-gray-600 shadow"
        >
          {{ generationError }}
        </p>

        <HealthJourneyPlanEditor
          v-if="proposal"
          :proposal="proposal"
          :acts="acts"
          :generating="generating"
          :programming="programming"
          :stale="planStale"
          :recap-note="unplaced
            ? `${unplaced} rendez-vous n'ont pas de créneau et ne seront pas programmés. ${recapNote}`
            : recapNote"
          @pin="handlePin"
          @unpin="handleUnpin"
          @remove="handleRemove"
          @recompute="generate(true)"
          @submit="submit"
        />

        <div
          v-else
          class="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded-lg bg-white p-10 text-center shadow"
        >
          <Sparkles class="size-6 text-primary" />
          <p class="text-sm text-gray-600">
            Choisissez un patient et les programmes à couvrir, puis générez le parcours.
          </p>
          <p class="text-xs text-gray-500">
            Les rendez-vous sont proposés sur les créneaux les plus calmes, avec le bon collaborateur,
            en respectant les délais réglementaires entre les actes.
          </p>
        </div>
      </div>
    </div>

    <HealthJourneyDoneDialog
      v-if="programmed && customer"
      v-model:open="doneOpen"
      :count="programmed.count"
      :customer-id="customer.id"
      :recap-notice="programmed.notice"
      @done="leave"
    />
  </div>
</template>
